import { useSearchParams } from 'react-router';
import { filters } from '../../utils';
import {
    LayerIcon,
    EllipseHalfIcon,
    CalendarIcon,
    WorldIcon,
    ElementsIcon,
    JournalIcon,
    ClockIcon,
    LocationIcon,
} from '@bi-digital/icons';
import type { CheckboxProps } from '@bi-digital/react';
import { FilterGroup, Checkbox, CheckboxSubsection, useCheckboxGroup } from '@bi-digital/react';
import { type SearchTypes, toSearchType } from '../../types';
import { useTranslation } from 'react-i18next';
import { useCallback, useRef, type ChangeEvent } from 'react';

export type FiltersProps = {
    /** Force a SearchType, if not set, type will be inferred from the URLSearchParams */
    type?: SearchTypes;
};

export const Filters = ({ type: forceType }: FiltersProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const type: SearchTypes = forceType || toSearchType(searchParams.get('type'));

    const getCheckboxProps = useCallback(
        (name: string, value: string): Partial<CheckboxProps> => {
            return {
                name,
                value,
                defaultChecked: searchParams.has(name, value),
                onChange: (e: ChangeEvent<HTMLInputElement>) =>
                    setSearchParams((prev) => {
                        if (e.target.checked) {
                            prev.append(e.target.name, e.target.value);
                        } else {
                            prev.delete(e.target.name, e.target.value);
                        }
                        return prev;
                    }),
            };
        },
        [searchParams, setSearchParams],
    );

    switch (type) {
        case 'Programmes':
            return <ProgrammesFilters getCheckboxProps={getCheckboxProps} />;
        case 'Articles':
            return <ArticlesFilters getCheckboxProps={getCheckboxProps} />;
        case 'Employees':
            return <EmployeesFilters getCheckboxProps={getCheckboxProps} />;
        case 'Events':
            return <EventsFilters getCheckboxProps={getCheckboxProps} />;
        default:
            return null;
    }
};

type SpecificFiltersProps = {
    getCheckboxProps: (name: string, value: string) => Partial<CheckboxProps>;
};

const ProgrammesFilters = ({ getCheckboxProps }: SpecificFiltersProps) => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const masterCoursesRef = useRef<HTMLInputElement>(null);
    const bachelorCoursesRef = useRef<HTMLInputElement>(null);

    const { checkboxGroupProps } = useCheckboxGroup({
        options: [
            searchParams.has(filters.programmes.level.name, filters.programmes.level.MasterCourses),
            searchParams.has(filters.programmes.level.name, filters.programmes.level.BachelorCourses),
        ],
        updateOptions: (checked) => {
            setSearchParams((prev) => {
                if (checked) {
                    if (!prev.has(filters.programmes.level.name, filters.programmes.level.BachelorCourses)) {
                        prev.append(filters.programmes.level.name, filters.programmes.level.BachelorCourses);
                        bachelorCoursesRef.current!.checked = true;
                    }
                    if (!prev.has(filters.programmes.level.name, filters.programmes.level.MasterCourses)) {
                        prev.append(filters.programmes.level.name, filters.programmes.level.MasterCourses);
                        masterCoursesRef.current!.checked = true;
                    }
                } else {
                    prev.delete(filters.programmes.level.name, filters.programmes.level.BachelorCourses);
                    prev.delete(filters.programmes.level.name, filters.programmes.level.MasterCourses);
                    bachelorCoursesRef.current!.checked = false;
                    masterCoursesRef.current!.checked = false;
                }
                return prev;
            });
        },
    });

    return (
        <div>
            <FilterGroup
                icon={<LayerIcon />}
                label={t(`programme.levelFilter`, { count: searchParams.getAll(filters.programmes.level.name).length })}
                defaultOpen
            >
                {filters.programmes.level.options.map((level) => (
                    <Checkbox
                        key={level}
                        {...getCheckboxProps(filters.programmes.level.name, level)}
                        label={t(`programme.levels.${level}`)}
                    />
                ))}
                <Checkbox label={t(`programme.levels.AllCourses`)} {...checkboxGroupProps} />
                <CheckboxSubsection>
                    <Checkbox
                        ref={masterCoursesRef}
                        {...getCheckboxProps(filters.programmes.level.name, filters.programmes.level.MasterCourses)}
                        label={t(`programme.levels.${filters.programmes.level.MasterCourses}`)}
                        fullwidth
                    />
                    <Checkbox
                        ref={bachelorCoursesRef}
                        {...getCheckboxProps(filters.programmes.level.name, filters.programmes.level.BachelorCourses)}
                        label={t(`programme.levels.${filters.programmes.level.BachelorCourses}`)}
                        fullwidth
                    />
                </CheckboxSubsection>
            </FilterGroup>
            <FilterGroup
                icon={<EllipseHalfIcon />}
                label={t(`programme.loadFilter`, { count: searchParams.getAll(filters.programmes.load.name).length })}
                defaultOpen={searchParams.getAll(filters.programmes.load.name).length > 0}
            >
                {filters.programmes.load.options.map((load) => (
                    <Checkbox
                        key={load}
                        {...getCheckboxProps(filters.programmes.load.name, load)}
                        label={t(`programme.loads.${load}`)}
                    />
                ))}
            </FilterGroup>
            <FilterGroup
                icon={<LocationIcon />}
                label={t(`programme.campusFilter`, {
                    count: searchParams.getAll(filters.programmes.campus.name).length,
                })}
                defaultOpen={searchParams.getAll(filters.programmes.campus.name).length > 0}
            >
                {filters.programmes.campus.options.map((campus) => (
                    <Checkbox
                        key={campus}
                        {...getCheckboxProps(filters.programmes.campus.name, campus)}
                        label={t(`programme.campuses.${campus}`)}
                    />
                ))}
            </FilterGroup>
            <FilterGroup
                icon={<CalendarIcon />}
                label={t(`programme.semesterFilter`, {
                    count: searchParams.getAll(filters.programmes.semester.name).length,
                })}
                defaultOpen={searchParams.getAll(filters.programmes.semester.name).length > 0}
            >
                {filters.programmes.semester.options.map((semester) => (
                    <Checkbox
                        key={semester}
                        {...getCheckboxProps(filters.programmes.semester.name, semester)}
                        label={t(`programme.semesters.${semester}`, { year: null })}
                    />
                ))}
            </FilterGroup>
            <FilterGroup
                icon={<WorldIcon />}
                label={t(`programme.languageFilter`, {
                    count: searchParams.getAll(filters.programmes.language.name).length,
                })}
                defaultOpen={searchParams.getAll(filters.programmes.language.name).length > 0}
            >
                {filters.programmes.language.options.map((language) => (
                    <Checkbox
                        key={language}
                        {...getCheckboxProps(filters.programmes.language.name, language)}
                        label={t(`programme.languages.${language}`)}
                    />
                ))}
            </FilterGroup>
            <FilterGroup
                icon={<ElementsIcon />}
                label={t(`programme.academicFieldFilter`, {
                    count: searchParams.getAll(filters.programmes.academicField.name).length,
                })}
                defaultOpen={searchParams.getAll(filters.programmes.academicField.name).length > 0}
            >
                {[...filters.programmes.academicField.options]
                    .sort((a, b) =>
                        t(`programme.academicFields.${a}`).localeCompare(t(`programme.academicFields.${b}`)),
                    )
                    .map((academicField) => (
                        <Checkbox
                            key={academicField}
                            {...getCheckboxProps(filters.programmes.academicField.name, academicField)}
                            label={t(`programme.academicFields.${academicField}`)}
                        />
                    ))}
            </FilterGroup>
        </div>
    );
};

const currentYear = new Date().getFullYear();
const years = Array.from({ length: currentYear - 2019 + 1 }, (_, i) => 2019 + i);

const ArticlesFilters = ({ getCheckboxProps }: SpecificFiltersProps) => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();

    return (
        <div>
            <FilterGroup
                icon={<JournalIcon />}
                label={t(`article.typeFilter`, { count: searchParams.getAll(filters.articles.type.name).length })}
                defaultOpen
            >
                {filters.articles.type.options.map((semester) => (
                    <Checkbox
                        key={semester}
                        {...getCheckboxProps(filters.articles.type.name, semester)}
                        label={t(`article.types.${semester}`)}
                    />
                ))}
            </FilterGroup>
            <FilterGroup
                icon={<ClockIcon />}
                label={t(`article.yearFilter`, { count: searchParams.getAll(filters.articles.year.name).length })}
                defaultOpen={searchParams.getAll(filters.articles.year.name).length > 0}
            >
                {years.map((year) => (
                    <Checkbox
                        key={year}
                        {...getCheckboxProps(filters.articles.year.name, year.toString())}
                        label={year}
                    />
                ))}
            </FilterGroup>
        </div>
    );
};

const EmployeesFilters = ({ getCheckboxProps }: SpecificFiltersProps) => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();

    return (
        <div>
            <FilterGroup
                icon={<LocationIcon />}
                label={t(`employee.campusFilter`, { count: searchParams.getAll(filters.employees.campus.name).length })}
                defaultOpen
            >
                {filters.employees.campus.options.map((campus) => (
                    <Checkbox
                        key={campus}
                        {...getCheckboxProps(filters.employees.campus.name, campus)}
                        label={t(`employee.campuses.${campus}`)}
                    />
                ))}
            </FilterGroup>
        </div>
    );
};

const EventsFilters = ({ getCheckboxProps }: SpecificFiltersProps) => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();

    return (
        <div>
            <FilterGroup
                icon={<CalendarIcon />}
                label={t(`event.typeFilter`, { count: searchParams.getAll(filters.events.type.name).length })}
                defaultOpen
            >
                {filters.events.type.options.map((type) => (
                    <Checkbox
                        key={type}
                        {...getCheckboxProps(filters.events.type.name, type)}
                        label={t(`event.types.${type}`)}
                    />
                ))}
            </FilterGroup>
            <FilterGroup
                icon={<LocationIcon />}
                label={t(`event.campusFilter`, { count: searchParams.getAll(filters.events.campus.name).length })}
                defaultOpen={searchParams.getAll(filters.events.campus.name).length > 0}
            >
                {filters.events.campus.options.map((campus) => (
                    <Checkbox
                        key={campus}
                        {...getCheckboxProps(filters.events.campus.name, campus)}
                        label={t(`event.campuses.${campus}`)}
                    />
                ))}
            </FilterGroup>
        </div>
    );
};
